import React, { useEffect, useState } from "react";
import { Link, Outlet } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/PageParts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Scale, Handshake, Webhook, Radio, ScrollText, ChevronRight, ArrowLeft, Mail, ShieldCheck, Trash2, Hash, Palette, ShieldAlert, UserCog } from "lucide-react";
import { useAuth } from "@/lib/auth";

const SETTING_GROUPS = [
  { id: "finance", title: "Finance Configuration", items: [
    { id: "tax", title: "SST & FX", description: "Prepare SST worksheets and manage reporting exchange rates.", to: "/settings/tax", icon: Scale },
    { id: "agency", title: "Agency & Commissions", description: "Manage agencies, closers, commissions and reconciliations.", to: "/settings/agency", icon: Handshake },
  ] },
  { id: "integrations", title: "Integrations", items: [
    { id: "webhooks", title: "Payment Webhooks", description: "Review gateway deliveries, processing results and webhook URLs.", to: "/settings/payment-webhooks", icon: Webhook, roles: ["superadmin", "accounts"] },
    { id: "elitebase", title: "EliteBase", description: "Manage onboarding events, finance readback and access commands.", to: "/settings/elitebase", icon: Radio },
  ] },
  { id: "communications", title: "Communications", roles: ["superadmin"], items: [
    { id: "email", title: "Email Settings", description: "Save sender preferences and follow the domain setup guide.", to: "/settings/email", icon: Mail, roles: ["superadmin"] },
  ] },
  { id: "documents", title: "Documents & Branding", roles: ["superadmin"], items: [
    { id: "branding", title: "Invoice, Pro-forma & Receipt Branding", description: "Upload your logo, set brand colour, company address, tax numbers, footer and bank details used on every PDF.", to: "/settings/branding", icon: Palette, roles: ["superadmin"] },
  ] },
  { id: "access", title: "Access Control", roles: ["superadmin"], items: [
    { id: "permissions", title: "Roles & Permissions", description: "Toggle capabilities for Admin and Account roles. Superadmin has full access.", to: "/settings/permissions", icon: ShieldAlert, roles: ["superadmin"] },
    { id: "users", title: "Users", description: "View all registered users, change roles, activate or deactivate accounts.", to: "/settings/users", icon: UserCog, roles: ["superadmin"] },
  ] },
  { id: "administration", title: "System & Administration", items: [
    { id: "audit", title: "Audit log", description: "Review the history of staff actions and system changes.", to: "/settings/audit", icon: ScrollText },
    { id: "number-series", title: "Document Numbering", description: "Customise how sales, pro-formas, invoices and receipts are numbered (prefix, year, padding, reset).", to: "/settings/number-series", icon: Hash, roles: ["superadmin"] },
    { id: "qa-cleanup", title: "QA Data Cleanup", description: "Back up and remove only inspected test records; preserve real data.", to: "/settings/qa-cleanup", icon: ShieldCheck, roles: ["superadmin"] },
    { id: "admin-cleanup", title: "Admin Cleanup", description: "Delete any record from any collection. Cascade removes linked children in one click.", to: "/settings/admin-cleanup", icon: Trash2, roles: ["superadmin"] },
  ] },
];

const SettingsGroup = ({ group, role }) => (
  <section aria-labelledby={`settings-heading-${group.id}`} data-testid={`settings-group-${group.id}`}>
    <h2 id={`settings-heading-${group.id}`} className="text-base md:text-lg font-heading font-bold text-slate-900 mb-3" data-testid={`settings-heading-${group.id}`}>{group.title}</h2>
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      {group.items.filter((item) => !item.roles || item.roles.includes(role)).map(({ id, title, description, to, icon: Icon }) => (
        <Link key={id} to={to} data-testid={`settings-card-${id}`} aria-labelledby={`settings-title-${id}`} aria-describedby={`settings-description-${id}`} className="group flex items-center gap-4 p-5 bg-white border border-slate-200 rounded-md shadow-sm transition-[border-color,box-shadow,background-color] duration-150 hover:border-slate-300 hover:bg-slate-50 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2">
          <span className="w-10 h-10 shrink-0 rounded-md bg-slate-100 text-slate-600 flex items-center justify-center"><Icon className="w-5 h-5" strokeWidth={1.75} aria-hidden="true" /></span>
          <div className="min-w-0 flex-1">
            <h3 id={`settings-title-${id}`} className="font-semibold text-sm text-slate-900" data-testid={`settings-title-${id}`}>{title}</h3>
            <p id={`settings-description-${id}`} className="text-xs text-slate-500 mt-1" data-testid={`settings-description-${id}`}>{description}</p>
          </div>
          <ChevronRight className="w-4 h-4 shrink-0 text-slate-400 transition-transform group-hover:translate-x-1" aria-hidden="true" />
        </Link>
      ))}
    </div>
  </section>
);

export const SettingsSection = () => (
  <>
    <nav aria-label="Settings navigation" className="px-6 pt-5" data-testid="settings-section-navigation">
      <Link to="/settings" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 rounded" data-testid="settings-back-link"><ArrowLeft className="w-4 h-4" aria-hidden="true" />Back to Settings</Link>
    </nav>
    <Outlet />
  </>
);

export default function Settings() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/auth/users"); setRows(data); } catch { setRows([]); }
  };
  useEffect(() => { load(); }, []);

  const isSuperadmin = user?.role === "superadmin";

  return (
    <>
      <PageHeader title="Settings" subtitle="Finance configuration, integrations and team administration." />
      <PageBody className="space-y-8">
        {SETTING_GROUPS.filter((group) => !group.roles || group.roles.includes(user?.role)).map((group) => <SettingsGroup key={group.id} group={group} role={user?.role} />)}
        <section aria-labelledby="settings-team-heading" data-testid="settings-team-section">
          <div className="flex items-center justify-between gap-4 mb-3">
            <div>
              <h2 id="settings-team-heading" className="text-base md:text-lg font-heading font-bold text-slate-900" data-testid="settings-team-heading">Team members</h2>
              <p className="text-xs text-slate-500 mt-1" data-testid="settings-team-description">Manage team members and roles. Only Superadmin can add or update users.</p>
            </div>
            {isSuperadmin && <Button onClick={() => setOpen(true)} data-testid="new-user-button"><Plus className="w-4 h-4 mr-1.5" /> Invite user</Button>}
          </div>
        <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Active</TableHead><TableHead>Created</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8">Only Owner and Superadmin can list users.</TableCell></TableRow>}
              {rows.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                  <TableCell>{u.name}</TableCell>
                  <TableCell className="text-xs font-mono">{u.email}</TableCell>
                  <TableCell><Badge variant="outline">{u.role}</Badge></TableCell>
                  <TableCell>{u.active ? "Yes" : "No"}</TableCell>
                  <TableCell className="text-xs text-slate-500 font-mono">{fmtDate(u.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        </section>
      </PageBody>

      <NewUserDialog open={open} onOpenChange={setOpen} onCreated={() => { setOpen(false); load(); }} />
    </>
  );
}

function NewUserDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "admin" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/auth/users", { ...form, email: form.email.toLowerCase().trim() });
      toast.success("User invited");
      setForm({ email: "", password: "", name: "", role: "admin" });
      onCreated();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Invite user</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs uppercase">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></div>
          <div><Label className="text-xs uppercase">Full name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></div>
          <div><Label className="text-xs uppercase">Temporary password</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" /></div>
          <div><Label className="text-xs uppercase">Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="account">Account</SelectItem>
                <SelectItem value="superadmin">Superadmin</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="user-submit">{saving ? "Saving…" : "Invite"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
